/**
 * The public site's header, footer and shell.
 *
 * Everything visible here comes from the database — the company name, the strapline,
 * the phone number, the navigation, the footer columns, the payment badges. Nothing is
 * typed into this file that a person might reasonably want to change, because the whole
 * premise of the system is that they change it in the back office rather than ringing
 * their developer.
 *
 * The values arrive from a single `/public/bootstrap` call held in a long-lived query,
 * so the chrome does not re-fetch on every navigation.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useCart } from '../lib/stores.js';
import { api } from '../lib/api.js';
import { useBootstrap, setting, whatsappLink } from '../lib/queries.js';
import { useReveal } from '../lib/reveal.js';

/* -------------------------------------------------------------------------- */
/* Logo                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The mark plus a typeset name — not the supplied wordmark image.
 *
 * The company's own wordmark PNG sets the name in WHITE, which is invisible against
 * this design's `--panel` header. Typesetting the name instead keeps it crisp at every
 * size, lets it follow the ink token onto a dark ground, and means a screen reader
 * announces the company rather than "image".
 */
function Logo({ compact = false }: { compact?: boolean }) {
  const { data } = useBootstrap();
  const name = setting(data, 'brand.short_name', 'Serendib IT');
  const locality = setting(data, 'brand.locality', '');
  const logoUrl = setting(data, 'brand.logo_url', '/brand/mark.png');

  return (
    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink)' }}>
      <img
        src={logoUrl}
        alt=""
        aria-hidden="true"
        width={30}
        height={30}
        style={{ borderRadius: 7, flex: 'none' }}
      />
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
        <span style={{ fontWeight: 700, fontSize: 16.5, letterSpacing: '-0.01em' }}>{name}</span>
        {locality && !compact ? (
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{locality}</span>
        ) : null}
      </span>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                      */
/* -------------------------------------------------------------------------- */

export function SiteHeader() {
  const { data } = useBootstrap();
  const cartCount = useCart((s) => s.lines.reduce((n, l) => n + l.quantity, 0));
  const storeOpen = setting<boolean>(data, 'store.enabled', true) !== false;
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  const items = (data?.nav ?? []).filter((n) => n.location === 'HEADER');

  // Close on navigation. Without this the panel stays open over the page you just asked
  // for, and the only way out is the button you have already stopped looking at.
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.search]);

  // Escape closes it, and the page behind does not scroll while it is open — a menu you
  // can scroll past is a menu that appears to have vanished.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    /*
     * The lock goes on the ROOT, not on `body`.
     *
     * `body { overflow: hidden }` is the usual way to do this and it silently failed
     * here, taking the sticky header with it. Body's overflow only propagates to the
     * viewport while the root's own overflow is `visible`, and ours is not — `html`
     * carries `overflow-x: clip` (see base.css) as the backstop for the marquee. So
     * body stopped propagating and instead became its own scroll container: the page
     * still scrolled via `html`, the "lock" did nothing, and `.header-dock` — whose
     * nearest scrolling ancestor was now the non-scrolling body — stopped sticking and
     * scrolled away. Measured on a 375px viewport: the dock sat at top 0 with the menu
     * closed and at -1264 the moment it opened.
     *
     * Locking the root stops the viewport scrolling for real and leaves the sticky
     * header resolving against the viewport, where it works.
     */
    const root = document.documentElement;
    const previousOverflow = root.style.overflowY;
    const previousPadding = root.style.paddingRight;
    // Removing the scrollbar would shift the page sideways; hold the gap open.
    const scrollbar = window.innerWidth - root.clientWidth;
    root.style.overflowY = 'hidden';
    if (scrollbar > 0) root.style.paddingRight = `${scrollbar}px`;
    return () => {
      document.removeEventListener('keydown', onKey);
      root.style.overflowY = previousOverflow;
      root.style.paddingRight = previousPadding;
    };
  }, [menuOpen]);

  return (
    <div className="header-dock">
      <header className="header-bar">
        <Logo />

        <nav className="header-nav" aria-label="Main">
          {items.map((item) => (
            <NavLink
              key={item.id}
              to={item.href}
              target={item.opensInNewTab ? '_blank' : undefined}
              rel={item.opensInNewTab ? 'noreferrer noopener' : undefined}
              className={({ isActive }) => `header-link ${isActive ? 'is-active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="header-actions">
          {storeOpen ? (
            <Link to="/cart" className="header-cart" aria-label={`Cart, ${cartCount} item${cartCount === 1 ? '' : 's'}`}>
              <span className="header-cart__label">Cart</span>
              {/* Rendered even at zero so the control does not change width when the
                  first item is added, which shifts the whole header. */}
              <span className="header-cart__badge" data-filled={cartCount > 0}>{cartCount}</span>
            </Link>
          ) : null}

          <Link to="/contact" className="btn btn--primary btn--sm header-cta">Start a project</Link>

          <button
            type="button"
            className="header-toggle"
            aria-expanded={menuOpen}
            aria-controls="site-menu"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <span className="header-toggle__bars" data-open={menuOpen}><i /><i /><i /></span>
          </button>
        </div>
      </header>

      {menuOpen ? (
        <>
          <button type="button" className="menu-scrim" aria-hidden="true" tabIndex={-1} onClick={() => setMenuOpen(false)} />
          <div className="menu-panel" id="site-menu">
            <nav aria-label="Main">
              {items.map((item) => (
                <NavLink key={item.id} to={item.href} className={({ isActive }) => `menu-link ${isActive ? 'is-active' : ''}`}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <Link to="/contact" className="btn btn--primary btn--block" style={{ marginTop: 14 }}>Start a project</Link>
          </div>
        </>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Footer                                                                      */
/* -------------------------------------------------------------------------- */

export function SiteFooter() {
  const { data } = useBootstrap();

  const company = setting(data, 'brand.company_name', 'Serendib IT Solutions');
  const blurb = setting(data, 'brand.footer_blurb', '');
  const phone = setting(data, 'contact.phone', '');
  const phoneLink = setting(data, 'contact.phone_link', '');
  const email = setting(data, 'contact.email', '');
  const line1 = setting(data, 'contact.address_line1', '');
  const line2 = setting(data, 'contact.address_line2', '');
  const whatsapp = whatsappLink(data);
  const badges = setting<string[]>(data, 'store.payment_badges', []);
  const holder = setting(data, 'brand.copyright_holder', company);

  const primary = (data?.nav ?? []).filter((n) => n.location === 'FOOTER_PRIMARY');
  const secondary = (data?.nav ?? []).filter((n) => n.location === 'FOOTER_SECONDARY');
  const legal = (data?.nav ?? []).filter((n) => n.location === 'LEGAL');

  const socials = (
    [
      ['Facebook', setting(data, 'social.facebook', '')],
      ['Instagram', setting(data, 'social.instagram', '')],
      ['LinkedIn', setting(data, 'social.linkedin', '')],
      ['X', setting(data, 'social.x', '')],
      ['WhatsApp', setting(data, 'social.whatsapp', '')],
    ] as [string, string][]
  ).filter(([, href]) => Boolean(href));

  return (
    <footer className="section wrap" style={{ paddingBottom: 20 }}>
      {/* The footer is a panel like any other, so it takes the panel step of the scale
          (tokens.css) rather than a value of its own. Its own padding did not shrink on
          a phone, which left it 44px inside while the panels directly above it were
          22px. */}
      <div className="panel" style={{ paddingBottom: 32 }}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 36 }}>
          <div className="stack" style={{ gap: 14 }}>
            <div className="row" style={{ gap: 10 }}>
              <img src="/brand/mark.png" alt="" aria-hidden="true" width={30} height={30} style={{ borderRadius: 7 }} />
              <span style={{ fontWeight: 700, fontSize: 16.5, color: 'var(--ink)' }}>{company}</span>
            </div>
            {blurb ? <p className="small" style={{ color: 'var(--ink-2)', maxWidth: '38ch' }}>{blurb}</p> : null}
            <div className="stack" style={{ gap: 5 }}>
              {phone ? (
                <a className="small" href={`tel:${phoneLink || phone}`} style={{ color: 'var(--ink-2)' }}>{phone}</a>
              ) : null}
              {email ? <a className="small" href={`mailto:${email}`} style={{ color: 'var(--ink-2)' }}>{email}</a> : null}
              {/* On every page, because WhatsApp is the channel most buyers here reach for
                  first and the footer is the one block that follows them around. Hidden
                  entirely until a number is configured — see `whatsappLink`. */}
              {whatsapp ? (
                <a className="small" href={whatsapp} target="_blank" rel="noreferrer noopener" style={{ color: 'var(--accent-deep)' }}>
                  WhatsApp ↗
                </a>
              ) : null}
              {line1 ? <p className="small" style={{ color: 'var(--ink-2)' }}>{line1}<br />{line2}</p> : null}
            </div>
          </div>

          <FooterColumn title="Navigation" items={primary} />
          <FooterColumn title="Hardware categories" items={secondary} />

          <div className="stack" style={{ gap: 12 }}>
            <h4 className="xs" style={{ color: 'var(--ink)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              Stay updated
            </h4>
            <p className="small" style={{ color: 'var(--ink-2)' }}>
              Sometimes we send notes about new hardware and what we have installed.
            </p>

            <NewsletterForm />

            {socials.length > 0 ? (
              <div className="row" style={{ gap: 8, marginTop: 4 }}>
                {socials.map(([label, href]) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="pill"
                    style={{ background: 'var(--wash)', color: 'var(--ink-2)' }}
                  >
                    {label}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div
          className="spread"
          style={{ marginTop: 36, paddingTop: 22, borderTop: '1px solid var(--hairline)', gap: 16 }}
        >
          <p className="xs" style={{ color: 'var(--ink-3)' }}>
            © {new Date().getFullYear()} {holder}. All rights reserved.
          </p>

          {badges.length > 0 ? (
            <div className="row" style={{ gap: 6 }}>
              <span className="xs" style={{ color: 'var(--ink-3)' }}>Payment on invoice:</span>
              {badges.map((badge) => (
                <span
                  key={badge}
                  className="xs"
                  style={{ border: '1px solid var(--hairline-strong)', borderRadius: 6, padding: '3px 8px', color: 'var(--ink-2)' }}
                >
                  {badge}
                </span>
              ))}
            </div>
          ) : null}

          <div className="row" style={{ gap: 14 }}>
            {legal.map((item) => (
              <Link key={item.id} to={item.href} className="xs" style={{ color: 'var(--ink-3)' }}>
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * The footer sign-up.
 *
 * Its own component so the failure and success states are local — a footer that
 * re-renders the whole page to show "thank you" would scroll the reader back to the top
 * of whatever they were reading.
 *
 * The honeypot is the same one the enquiry forms use: hidden from people, filled in by
 * bots, and answered with a success the bot never learns was discarded.
 */
function NewsletterForm() {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [honeypot, setHoneypot] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState('sending');
    const email = String(new FormData(event.currentTarget).get('email') ?? '');
    try {
      await api.post('/public/subscribe', { email, website: honeypot }, { auth: false });
      setState('sent');
    } catch {
      setState('error');
    }
  }

  if (state === 'sent') {
    return (
      <p className="small" style={{ color: 'var(--accent)' }} role="status">
        You are on the list. We send rarely.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="footer-signup">
      <div aria-hidden="true" style={{ position: 'absolute', left: -9999, width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="footer-website">Leave this empty</label>
        <input id="footer-website" name="website" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>

      <label className="sr-only" htmlFor="footer-email">Email address</label>
      <input id="footer-email" name="email" type="email" required placeholder="Enter your email" />
      <button type="submit" disabled={state === 'sending'}>
        {state === 'sending' ? '…' : 'Join'}
      </button>

      {state === 'error' ? (
        <p className="xs" style={{ color: '#F08A7A', gridColumn: '1 / -1' }} role="alert">
          That did not go through. Try again, or email us.
        </p>
      ) : null}
    </form>
  );
}

function FooterColumn({ title, items }: { title: string; items: { id: number; label: string; href: string }[] }) {
  if (items.length === 0) return null;
  return (
    <div className="stack" style={{ gap: 10 }}>
      <h4 className="xs" style={{ color: 'var(--ink)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {title}
      </h4>
      <ul className="stack" style={{ gap: 7 }}>
        {items.map((item) => (
          <li key={item.id}>
            <Link to={item.href} className="small" style={{ color: 'var(--ink-2)' }}>
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Shell                                                                       */
/* -------------------------------------------------------------------------- */

export function SiteLayout({ children }: { children: ReactNode }) {
  const { data } = useBootstrap();
  const maintenance = setting<boolean>(data, 'integrations.maintenance_mode', false) === true;

  // Sections arrive as you scroll to them. Does nothing under `prefers-reduced-motion`,
  // and never hides content if the script does not run — see `lib/reveal.ts`.
  useReveal();

  if (maintenance) return <MaintenanceScreen />;

  return (
    <div className="shell">
      {/* The keyboard user's first stop: without it, reaching the content means
          tabbing through every navigation item on every page. */}
      <a className="skip-link" href="#main">Skip to content</a>
      <SiteHeader />
      <main id="main" style={{ flex: 1 }}>{children}</main>
      <SiteFooter />
    </div>
  );
}

function MaintenanceScreen() {
  const { data } = useBootstrap();
  return (
    <div className="shell" style={{ placeContent: 'center', display: 'grid', padding: 20 }}>
      <div className="panel" style={{ maxWidth: 520, textAlign: 'center', display: 'grid', gap: 14, justifyItems: 'center' }}>
        <img src="/brand/mark.png" alt="" width={44} height={44} style={{ borderRadius: 10 }} />
        <h1 style={{ fontSize: 'var(--t-h2)' }}>{setting(data, 'brand.company_name', 'Serendib IT Solutions')}</h1>
        <p className="lead">{setting(data, 'integrations.maintenance_message', 'We will be back shortly.')}</p>
        <a className="btn btn--secondary" href={`tel:${setting(data, 'contact.phone_link', '')}`}>
          {setting(data, 'contact.phone', 'Call us')}
        </a>
      </div>
    </div>
  );
}

/** Scroll to the top on navigation. A router does not do this by itself. */
export function ScrollToTop() {
  const { pathname } = useLocation();
  if (typeof window !== 'undefined') {
    // Layout effect timing is not needed: the browser paints the new route at the
    // scroll position it had, and correcting it in the same tick is imperceptible.
    queueMicrotask(() => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }));
  }
  return <span data-path={pathname} hidden />;
}
